import React from "react";
import WizardStepper from "../../components/WizardStepper";
import AiDisclaimer from "../../components/AiDisclaimer";
import Button from "../../ui/Button";
import { Card, CardBody, CardHeader } from "../../ui/Card";
import RadioCard from "../../ui/RadioCard";
import Badge from "../../ui/Badge";
import Spinner from "../../ui/Spinner";
import Progress from "../../ui/Progress";
import {
  downloadEstimatorPackZip,
  ensureJob,
  getLatestSuggestionSet,
  normalizeSuggestionSetToRows,
  runReviewFromEstimate,
} from "../../api/review";

const STEP_IDS = {
  B0: "B0_UPLOAD_ESTIMATE",
  B1: "B1_STATUS_PROCESSING",
  B2: "B2_SUMMARY",
};

const WIZARD_STEPS = [
  { id: STEP_IDS.B0, label: "Upload Estimate" },
  { id: STEP_IDS.B1, label: "Status / Processing" },
  { id: STEP_IDS.B2, label: "Summary" },
];

const PROCESS_PHASES = ["Creating job", "Uploading", "Running", "Collecting results", "Ready"];
const STORAGE_KEY = "review_wizard_state_v2";

function createInitialState() {
  return {
    currentStepId: STEP_IDS.B0,
    jobId: "",
    setId: "",
    estimateFile: null,
    platformDetected: false,
    platform: "",
    viewAs: "",
    processingPhase: "Creating job",
    processingMessage: "",
    processingDone: false,
    processingError: "",
    reviewRunRequested: false,
    rows: [],
    latestSetPayload: null,
    downloadingZip: false,
  };
}

function safeLoadState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return createInitialState();
    const parsed = JSON.parse(raw);
    return { ...createInitialState(), ...parsed };
  } catch (e) {
    return createInitialState();
  }
}

function toFileMeta(file) {
  return {
    name: file.name,
    size: file.size,
    type: file.type,
  };
}

function phaseIndex(phase) {
  const idx = PROCESS_PHASES.indexOf(phase);
  return idx >= 0 ? idx : 0;
}

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function mapViewCode(row, viewAs) {
  if (viewAs === "symbility" && row.code && !String(row.code).startsWith("SYM_")) {
    return `SYM_${row.code}`;
  }
  if (viewAs === "xactimate" && String(row.code || "").startsWith("SYM_")) {
    return String(row.code).replace(/^SYM_/, "");
  }
  return row.code;
}

function classifyLineItemArea(row) {
  const text = `${String(row?.description || "")} ${String(row?.room || "")}`.toLowerCase();
  if (/ceiling|popcorn|overhead/.test(text)) return "Ceiling";
  if (/floor|tile|vinyl|carpet|pad|plank|mortar|subfloor/.test(text)) return "Floors";
  if (/wall|drywall|paint|texture|baseboard|casing|trim|crown/.test(text)) return "Walls";
  return "Other";
}

function formatRoomName(value) {
  const raw = String(value || "").trim();
  if (!raw) return "(Unspecified)";
  if (raw.startsWith("(") && raw.endsWith(")")) return "(Unspecified)";
  return raw
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
}

function groupRowsByRoomAndArea(rows) {
  const areaOrder = ["Walls", "Floors", "Ceiling", "Other"];
  const roomOrder = [];
  const roomMap = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const room = formatRoomName(String(row?.room || "").trim() || "(unspecified)");
    if (!roomMap.has(room)) {
      roomMap.set(room, { Walls: [], Floors: [], Ceiling: [], Other: [] });
      roomOrder.push(room);
    }
    const area = classifyLineItemArea(row);
    roomMap.get(room)[area].push(row);
  });
  return roomOrder.map((room) => ({
    room,
    areas: areaOrder
      .filter((area) => roomMap.get(room)[area].length > 0)
      .map((area) => ({ area, rows: roomMap.get(room)[area] })),
  }));
}

export default function ReviewWizard() {
  const [state, setState] = React.useState(() => safeLoadState());
  const [estimateFileObj, setEstimateFileObj] = React.useState(null);
  const uploadRef = React.useRef(null);
  const advanceTimerRef = React.useRef(null);

  React.useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  React.useEffect(() => {
    if (state.currentStepId !== STEP_IDS.B1 || state.reviewRunRequested) return;
    setState((prev) => ({ ...prev, reviewRunRequested: true, processingError: "" }));

    const run = async () => {
      try {
        const file = estimateFileObj;
        if (!file) throw new Error("Estimate PDF is required");

        setState((prev) => ({
          ...prev,
          processingPhase: "Creating job",
          processingMessage: "Ensuring job",
        }));
        const job = await ensureJob(state.jobId);
        const jobId = job.jobId;
        setState((prev) => ({ ...prev, jobId }));

        setState((prev) => ({
          ...prev,
          processingPhase: "Uploading",
          processingMessage: "Uploading estimate",
        }));
        setState((prev) => ({
          ...prev,
          processingPhase: "Running",
          processingMessage: "Running estimate review",
        }));
        const runResult = await runReviewFromEstimate({
          jobId,
          file,
          platform: state.platform || "xactimate",
          profileId: "default",
        });
        if (runResult.setId) {
          setState((prev) => ({ ...prev, setId: runResult.setId }));
        }

        setState((prev) => ({
          ...prev,
          processingPhase: "Collecting results",
          processingMessage: "Fetching latest suggestion set",
        }));

        let latest = null;
        for (let i = 0; i < 10; i += 1) {
          try {
            latest = await getLatestSuggestionSet(jobId, state.platform || "xactimate");
            const payload = latest?.payload || null;
            const rows = normalizeSuggestionSetToRows(payload, state.viewAs || state.platform || "xactimate");
            if (rows.length > 0) {
              setState((prev) => ({
                ...prev,
                setId: latest?.setId || prev.setId,
                latestSetPayload: payload,
                rows,
              }));
              break;
            }
          } catch (e) {
            // keep polling
          }
          await wait(400);
        }

        if (!latest) {
          throw new Error("Could not fetch the completed suggestion set. Please retry the review.");
        }
        const latestRows = normalizeSuggestionSetToRows(
          latest?.payload || null,
          state.viewAs || state.platform || "xactimate"
        );
        if (latestRows.length < 1) {
          throw new Error("The estimate review completed but produced no line item suggestions.");
        }

        setState((prev) => ({
          ...prev,
          processingDone: true,
          processingPhase: "Ready",
          processingMessage: "Review summary ready",
        }));

        advanceTimerRef.current = window.setTimeout(() => {
          setState((prev) => ({ ...prev, currentStepId: STEP_IDS.B2 }));
        }, 450);
      } catch (error) {
        setState((prev) => ({
          ...prev,
          processingError: error?.message || "Review run failed",
          processingDone: false,
          processingPhase: "Running",
          processingMessage: "Review failed",
        }));
      }
    };

    run();

    return () => {
      if (advanceTimerRef.current) {
        window.clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [
    estimateFileObj,
    state.currentStepId,
    state.jobId,
    state.platform,
    state.reviewRunRequested,
    state.viewAs,
  ]);

  const handleEstimateUpload = (event) => {
    const fileObj = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setEstimateFileObj(fileObj);
    setState((prev) => ({
      ...prev,
      estimateFile: fileObj ? toFileMeta(fileObj) : null,
      processingError: "",
    }));
  };

  const canContinueFromB0 = !!state.estimateFile && !!state.platform;

  const handleContinueFromB0 = async () => {
    try {
      const ensured = await ensureJob(state.jobId);
      setState((prev) => ({
        ...prev,
        jobId: ensured.jobId,
        currentStepId: STEP_IDS.B1,
        processingPhase: "Creating job",
        processingMessage: "Preparing review",
        processingDone: false,
        processingError: "",
        reviewRunRequested: false,
        viewAs: prev.viewAs || prev.platform || "xactimate",
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        processingError: error?.message || "Could not initialize review job",
      }));
    }
  };

  const handleViewAs = (value) => {
    setState((prev) => ({ ...prev, viewAs: value }));
    if (state.latestSetPayload) {
      const rows = normalizeSuggestionSetToRows(state.latestSetPayload, value);
      if (rows.length > 0) {
        setState((prev) => ({ ...prev, rows }));
      }
    }
  };

  const handleDownloadZip = async () => {
    try {
      setState((prev) => ({ ...prev, downloadingZip: true, processingError: "" }));
      const blob = await downloadEstimatorPackZip({
        jobId: state.jobId,
        setId: state.setId,
        platform: state.platform || "xactimate",
        includeAll: true,
        includeRationale: true,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "estimator-pack.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        processingError: error?.message || "ZIP download failed",
      }));
    } finally {
      setState((prev) => ({ ...prev, downloadingZip: false }));
    }
  };

  const retryReview = () => {
    setState((prev) => ({
      ...prev,
      reviewRunRequested: false,
      processingDone: false,
      processingError: "",
      processingPhase: "Running",
      processingMessage: "Retrying review",
    }));
  };

  const startAnotherReview = () => {
    if (uploadRef.current) uploadRef.current.value = "";
    setEstimateFileObj(null);
    setState(createInitialState());
  };

  const viewAs = state.viewAs || state.platform || "xactimate";
  const rows = Array.isArray(state.rows) ? state.rows : [];
  const groupedRows = React.useMemo(() => groupRowsByRoomAndArea(rows), [rows]);

  const renderB0 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Upload Estimate</h1>
        <p className="wizard-subtitle">Upload estimate PDF to start review.</p>
      </CardHeader>
      <CardBody>

        <input
          ref={uploadRef}
          id="review-estimate-upload"
          type="file"
          accept="application/pdf,.pdf"
          className="wizard-hidden-input"
          onChange={handleEstimateUpload}
        />
        <label className="wizard-upload-button small" htmlFor="review-estimate-upload">
          Upload Estimate PDF
        </label>

        {state.estimateFile ? (
          <div className="wizard-file-pill">
            <span>{state.estimateFile.name}</span>
          </div>
        ) : (
          <p className="wizard-muted">No estimate uploaded.</p>
        )}

        {!state.platformDetected ? (
          <div className="review-platform-prompt">
            <p className="wizard-subtitle">Choose estimate platform.</p>
            <div className="wizard-platform-grid">
              <RadioCard
                name="review-platform"
                value="xactimate"
                title="Xactimate"
                description="Use Xactimate formatted codes."
                checked={state.platform === "xactimate"}
                onChange={() => setState((prev) => ({ ...prev, platform: "xactimate" }))}
              />
              <RadioCard
                name="review-platform"
                value="symbility"
                title="Symbility"
                description="Use Symbility formatted codes."
                checked={state.platform === "symbility"}
                onChange={() => setState((prev) => ({ ...prev, platform: "symbility" }))}
              />
            </div>
          </div>
        ) : null}

        <div className="wizard-actions">
          <Button onClick={handleContinueFromB0} disabled={!canContinueFromB0}>
            Continue
          </Button>
        </div>
      </CardBody>
    </Card>
  );

  const renderB1 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Status / Processing</h1>
      </CardHeader>
      <CardBody>
      <div className="wizard-progress-wrap">
        <Progress value={((phaseIndex(state.processingPhase) + 1) / PROCESS_PHASES.length) * 100} />
        <Badge tone="info">{state.processingPhase}</Badge>
      </div>
      <ul className="wizard-processing-list">
        {PROCESS_PHASES.map((phase) => {
          const current = phaseIndex(state.processingPhase);
          const idx = phaseIndex(phase);
          const isDone = idx < current || (state.processingDone && phase === "Ready");
          const isActive = idx === current && !isDone;
          return (
            <li key={phase} className={`wizard-processing-item${isDone ? " done" : ""}`}>
              <span className={`wizard-processing-icon${isActive ? " active" : ""}`}>
                {isDone ? "OK" : <Spinner size="sm" />}
              </span>
              <span>{phase}</span>
            </li>
          );
        })}
      </ul>
      <p className="wizard-muted">{state.processingMessage || "Processing..."}</p>
      {state.processingError ? <p className="wizard-warning">{state.processingError}</p> : null}
      <div className="wizard-actions">
        {state.processingError ? (
          <Button onClick={retryReview}>
            Retry
          </Button>
        ) : null}
      </div>
      </CardBody>
    </Card>
  );

  const renderB2 = () => (
    <Card className="wizard-card">
      <CardHeader>
        <h1 className="wizard-title">Summary</h1>
        <p className="wizard-subtitle">Possible missing line item suggestions</p>
      </CardHeader>
      <CardBody>
      <AiDisclaimer
        title="Review Disclaimer"
        detail="Missing-item suggestions are AI-assisted and can be incomplete or incorrect. Confirm all scope, code mapping, and quantities before submission."
      />

      <div className="review-toggle-row">
        <span className="wizard-muted">View as:</span>
        <Button
          size="sm"
          variant={viewAs === "xactimate" ? "primary" : "secondary"}
          onClick={() => handleViewAs("xactimate")}
        >
          Xactimate
        </Button>
        <Button
          size="sm"
          variant={viewAs === "symbility" ? "primary" : "secondary"}
          onClick={() => handleViewAs("symbility")}
        >
          Symbility
        </Button>
      </div>

      <div className="space-y-4">
        {groupedRows.map((roomGroup) => (
          <div key={roomGroup.room} className="space-y-3">
            <h3 className="text-xl font-semibold tracking-tight text-zinc-900">
              Room: {roomGroup.room}
            </h3>
            {roomGroup.areas.map((areaGroup) => (
              <div key={`${roomGroup.room}-${areaGroup.area}`} className="review-table-wrap">
                <h4 className="wizard-subtitle px-3 pt-3">{areaGroup.area}</h4>
                <table className="review-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Unit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {areaGroup.rows.map((row) => (
                      <tr key={`${roomGroup.room}-${areaGroup.area}-${row.description}-${row.code}`}>
                        <td>{mapViewCode(row, viewAs)}</td>
                        <td>{row.description}</td>
                        <td>{row.qty}</td>
                        <td>{row.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="wizard-actions">
        <Button onClick={handleDownloadZip} disabled={state.downloadingZip || !state.jobId}>
          {state.downloadingZip ? "Preparing ZIP..." : "Download Estimator Pack (ZIP)"}
        </Button>
        <Button variant="secondary" onClick={startAnotherReview}>
          Start another review
        </Button>
      </div>
      {state.processingError ? <p className="wizard-warning">{state.processingError}</p> : null}
      </CardBody>
    </Card>
  );

  let content = null;
  if (state.currentStepId === STEP_IDS.B0) content = renderB0();
  else if (state.currentStepId === STEP_IDS.B1) content = renderB1();
  else content = renderB2();

  return (
    <div className="build-wizard-page">
      <WizardStepper steps={WIZARD_STEPS} currentStepId={state.currentStepId} />
      {content}
    </div>
  );
}
